import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalFooter,
   ModalBody,
   ModalCloseButton,
   Button,
   Input,
   Grid,
   GridItem,
   useColorModeValue,
   Flex,
   Heading,
   Box,
   HStack,
   PinInput,
   PinInputField,
   Image,
   IconButton,
   Text,
} from '@chakra-ui/react';
import Cropper from 'react-easy-crop';
import { CloseIcon, RepeatIcon } from '@chakra-ui/icons';
import { useDispatch } from 'react-redux';
import { uploadDocument, sendVerifyOtp, deleteDocument, getAccount } from '../../../../../redux/actions/account';
import { deleteSalesDocument, uploadSalesDocument } from '../../../../../redux/actions/salesInvoice';
import { deletePurchaseDocument, uploadPurchaseDocument } from '../../../../../redux/actions/purchaseInvoice';
import { deleteIssueDocument, uploadIssueDocument } from '../../../../../redux/actions/issueInventory';
import { deleteReceiveDocument, uploadReceiveDocument } from '../../../../../redux/actions/receiveInventory';
import { hideMobileNumber } from 'utils/util';
import _ from 'lodash';
import { useUser } from '../../../../../contexts';

// Helper function to create image from URL
const createImage = (url) =>
   new Promise((resolve, reject) => {
      const image = new window.Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => {
         console.error('Image load error:', error);
         reject(error);
      });
      // Try without crossOrigin first for blob URLs
      if (url.startsWith('blob:') || url.startsWith('data:')) {
         image.src = url;
      } else {
         image.setAttribute('crossOrigin', 'anonymous');
         image.src = url;
      }
   });

// Helper function to get cropped image - FIXED VERSION with WHITE background
async function getCroppedImg(imageSrc, pixelCrop, rotation = 0) {
   try {
      const image = await createImage(imageSrc);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { alpha: false }); // Disable alpha for white background

      if (!ctx) {
         throw new Error('Failed to get canvas context');
      }

      const rotRad = (rotation * Math.PI) / 180;

      // Calculate bounding box of the rotated image
      const { width: bBoxWidth, height: bBoxHeight } = rotateSize(image.width, image.height, rotation);

      // Set canvas size to match the bounding box
      canvas.width = bBoxWidth;
      canvas.height = bBoxHeight;

      // Fill canvas with WHITE background BEFORE drawing
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, bBoxWidth, bBoxHeight);

      // Translate canvas context to a central location to allow rotating around the center
      ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
      ctx.rotate(rotRad);
      ctx.translate(-image.width / 2, -image.height / 2);

      // Draw rotated image
      ctx.drawImage(image, 0, 0);

      // Create a new canvas for the cropped area
      const croppedCanvas = document.createElement('canvas');
      const croppedCtx = croppedCanvas.getContext('2d', { alpha: false }); // Disable alpha

      if (!croppedCtx) {
         throw new Error('Failed to get cropped canvas context');
      }

      // Set the size of the cropped canvas
      croppedCanvas.width = pixelCrop.width;
      croppedCanvas.height = pixelCrop.height;

      // Fill cropped canvas with WHITE background BEFORE drawing
      croppedCtx.fillStyle = '#FFFFFF';
      croppedCtx.fillRect(0, 0, pixelCrop.width, pixelCrop.height);

      // Draw the cropped image
      croppedCtx.drawImage(
         canvas,
         pixelCrop.x,
         pixelCrop.y,
         pixelCrop.width,
         pixelCrop.height,
         0,
         0,
         pixelCrop.width,
         pixelCrop.height,
      );

      // Convert canvas to blob
      return new Promise((resolve, reject) => {
         croppedCanvas.toBlob(
            (blob) => {
               if (!blob) {
                  console.error('Canvas toBlob failed - blob is null');
                  reject(new Error('Canvas is empty'));
                  return;
               }
               resolve(blob);
            },
            'image/jpeg',
            0.95,
         );
      });
   } catch (error) {
      console.error('Error in getCroppedImg:', error);
      throw error;
   }
}

// Helper function to calculate rotated size
function rotateSize(width, height, rotation) {
   const rotRad = (rotation * Math.PI) / 180;

   return {
      width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
      height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
   };
}

// Image Crop Modal Component - Optimized with React.memo
const ImageCropModal = React.memo(({ isOpen, onClose, imageSrc, onCropComplete, imageType, imageIndex }) => {
   const [crop, setCrop] = useState({ x: 0, y: 0 });
   const [zoom, setZoom] = useState(1);
   const [rotation, setRotation] = useState(0);
   const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
   const [isImageLoading, setIsImageLoading] = useState(true);
   const [imageLoadError, setImageLoadError] = useState(false);
   const [isProcessing, setIsProcessing] = useState(false); // ADD THIS STATE

   // Reset zoom and rotation when modal opens with new image
   useEffect(() => {
      if (isOpen) {
         setZoom(1);
         setRotation(0);
         setCrop({ x: 0, y: 0 });
         setIsImageLoading(true);
         setImageLoadError(false);
         setIsProcessing(false); // Reset processing state
      }
   }, [isOpen, imageSrc]);

   // Cleanup on unmount
   useEffect(() => {
      return () => {
         setIsProcessing(false);
      };
   }, []);

   const getAspectRatio = () => {
      if (imageType === 'user') {
         return 1;
      } else if (imageType === 'doc') {
         return 4.5 / 3;
      } else if (imageType === 'invoice') {
         return 3 / 4.5;
      }
      return 1;
   };

   const getCropShapeLabel = () => {
      if (imageType === 'user') {
         return 'Square (1:1)';
      } else if (imageType === 'doc') {
         return 'Document (4.5:3)';
      } else if (imageType === 'invoice') {
         return 'Invoice (3:4.5)';
      }
      return 'Square';
   };

   const onCropChange = (crop) => {
      setCrop(crop);
   };

   const onZoomChange = (zoom) => {
      setZoom(zoom);
   };

   const onRotationChange = (rotation) => {
      setRotation(rotation);
   };

   const onCropCompleteCallback = useCallback((croppedArea, croppedAreaPixels) => {
      setCroppedAreaPixels(croppedAreaPixels);
   }, []);

   const handleCropSave = useCallback(async () => {
      // Prevent multiple clicks
      if (isProcessing) {
         console.log('Already processing, ignoring click');
         return;
      }

      try {
         setIsProcessing(true); // Set processing flag

         if (!croppedAreaPixels) {
            console.error('No crop area selected');
            alert('Please wait for the image to load completely.');
            setIsProcessing(false);
            return;
         }

         const croppedImageBlob = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);

         if (croppedImageBlob) {
            onCropComplete(croppedImageBlob, imageType, imageIndex);
            onClose();
         }
      } catch (e) {
         console.error('Error cropping image:', e);
         alert('Failed to crop image: ' + e.message + '. Please try again.');
         setIsProcessing(false); // Reset on error
      }
   }, [isProcessing, croppedAreaPixels, imageSrc, rotation, onCropComplete, imageType, imageIndex, onClose]);

   const handleZoomIn = () => {
      setZoom((prevZoom) => Math.min(prevZoom + 0.1, 3));
   };

   const handleZoomOut = () => {
      setZoom((prevZoom) => Math.max(prevZoom - 0.1, 1));
   };

   const handleRotateLeft = () => {
      setRotation((prev) => (prev - 60) % 360);
   };

   const handleRotateRight = () => {
      setRotation((prev) => (prev + 60) % 360);
   };

   const handleResetAll = () => {
      setZoom(1);
      setRotation(0);
   };

   return (
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
         <ModalOverlay />
         <ModalContent bg={useColorModeValue('white', '#2d3748')} color={useColorModeValue('gray.800', 'white')}>
            <ModalHeader>Crop Image - {getCropShapeLabel()}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
               <Box position="relative" height="400px" bg="gray.100" borderRadius="md">
                  {isImageLoading && (
                     <Flex
                        position="absolute"
                        top="0"
                        left="0"
                        right="0"
                        bottom="0"
                        alignItems="center"
                        justifyContent="center"
                        bg="gray.100"
                        zIndex="1">
                        <Text>Loading image...</Text>
                     </Flex>
                  )}
                  {imageLoadError && (
                     <Flex
                        position="absolute"
                        top="0"
                        left="0"
                        right="0"
                        bottom="0"
                        alignItems="center"
                        justifyContent="center"
                        bg="gray.100"
                        zIndex="1">
                        <Text color="red.500">Failed to load image</Text>
                     </Flex>
                  )}
                  <Cropper
                     image={imageSrc}
                     crop={crop}
                     zoom={zoom}
                     rotation={rotation}
                     aspect={getAspectRatio()}
                     onCropChange={onCropChange}
                     onZoomChange={onZoomChange}
                     onRotationChange={onRotationChange}
                     onCropComplete={onCropCompleteCallback}
                     onMediaLoaded={() => {
                        setIsImageLoading(false);
                        setImageLoadError(false);
                     }}
                     onLoadError={(error) => {
                        console.error('Image load error:', error);
                        setIsImageLoading(false);
                        setImageLoadError(true);
                     }}
                     minZoom={0.1}
                     maxZoom={3}
                     restrictPosition={false}
                  />
               </Box>

               {/* Combined Zoom and Rotation Controls */}
               <Box mt={4}>
                  <Flex justifyContent="space-between" alignItems="center" mb={3}>
                     <Text fontWeight="medium">
                        Zoom: {zoom.toFixed(1)}x | Rotation: {rotation}°
                     </Text>
                     <Button size="xs" onClick={handleResetAll} variant="ghost" colorScheme="red">
                        Reset All
                     </Button>
                  </Flex>

                  <Flex gap={2} alignItems="center" wrap="wrap">
                     {/* Zoom Controls */}
                     <Button
                        size="sm"
                        onClick={handleZoomOut}
                        isDisabled={zoom <= 1}
                        colorScheme="blue"
                        variant="outline">
                        Zoom -
                     </Button>

                     <Button
                        size="sm"
                        onClick={handleZoomIn}
                        isDisabled={zoom >= 3}
                        colorScheme="blue"
                        variant="outline">
                        Zoom +
                     </Button>

                     {/* Rotation Controls */}
                     <Button
                        size="sm"
                        onClick={handleRotateLeft}
                        colorScheme="purple"
                        variant="outline"
                        leftIcon={<RepeatIcon transform="scaleX(-1)" />}>
                        60° Left
                     </Button>

                     <Button
                        size="sm"
                        onClick={handleRotateRight}
                        colorScheme="purple"
                        variant="outline"
                        rightIcon={<RepeatIcon />}>
                        60° Right
                     </Button>
                  </Flex>

                  <Text fontSize="xs" color="gray.500" mt={2}>
                     Use buttons to zoom and rotate the image. Click "Reset All" to restore default settings.
                  </Text>
               </Box>
            </ModalBody>
            <ModalFooter>
               <Button variant="ghost" mr={3} onClick={onClose} disabled={isProcessing}>
                  Cancel
               </Button>
               <Button
                  colorScheme="blue"
                  onClick={handleCropSave}
                  isLoading={isProcessing}
                  loadingText="Applying Crop..."
                  disabled={isProcessing}>
                  Apply Crop
               </Button>
            </ModalFooter>
         </ModalContent>
      </Modal>
   );
});

const CustomerVerifyModal = ({
   isOpen,
   onClose,
   accountId,
   entryData,
   handleSubmit,
   mobile,
   account,
   isLoading,
   entryType,
   invoiceImages = [],
   setInvoiceImages,
}) => {
   const [file, setFile] = useState(null);
   const [docImages, setDocImages] = useState([]);
   const [userImages, setUserImages] = useState([]);
   const [otp, setOtp] = useState(null);
   const [isSaveLoading, setIsSaveLoading] = useState(false);
   const [cropModalOpen, setCropModalOpen] = useState(false);
   const [currentCropImage, setCurrentCropImage] = useState(null);
   const [currentCropType, setCurrentCropType] = useState(null);
   const [currentCropIndex, setCurrentCropIndex] = useState(null);
   const [isRecropping, setIsRecropping] = useState(false);
   const [originalFile, setOriginalFile] = useState(null);
   const skipBtnRef = useRef(null);
   const dispatch = useDispatch();
   const { user } = useUser();

   useEffect(() => {
      if (isOpen && account) {
         const existingDocImages =
            account?.imagesLive?.docs?.map((url) => ({
               url,
               isExisting: true,
            })) || [];

         const existingUserImages =
            account?.imagesLive?.user?.map((url) => ({
               url,
               isExisting: true,
            })) || [];

         const existingInvoiceImages =
            entryData?.imagesLive?.bill?.map((url) => ({
               url,
               isExisting: true,
            })) || [];

         setDocImages(existingDocImages);
         setUserImages(existingUserImages);
         setInvoiceImages(existingInvoiceImages);
      }
   }, [isOpen, account, entryData, setInvoiceImages]);

   const saveImage = async () => {
      if (isSaveLoading) return;

      setIsSaveLoading(true);

      const formData = new FormData();
      if (docImages.length > 0 || userImages.length > 0) {
         formData.append('id', accountId);
         docImages.forEach((imgObj) => {
            if (!imgObj.isExisting && imgObj.file) {
               formData.append('docs', imgObj.file);
            }
         });
         userImages.forEach((imgObj) => {
            if (!imgObj.isExisting && imgObj.file) {
               formData.append('user', imgObj.file);
            }
         });
      }

      try {
         if (mobile && otp) {
            const res = await dispatch(sendVerifyOtp({ id: accountId, otp: Number(otp) }));
            if (res?.code === 1) {
               setFile(null);
               setOtp(null);
               handleSubmit();
               onClose();
            }
         }

         if (docImages?.length > 0 || userImages?.length > 0) {
            const response = await dispatch(uploadDocument(formData));

            if (response?.code === 1) {
               setFile(null);
               setOtp(null);
               setDocImages([]);
               setUserImages([]);
               handleSubmit();
               onClose();
            }
         }

         dispatch(
            getAccount({
               compId: Number(user?.loginhistory?.companyId),
               accountTypes: ['K', 'E', 'C', 'D', 'BROKER', 'I', 'L', 'M', 'N', 'A'].join(','),
               status: true,
            }),
         );
      } catch (error) {
         console.error('Error in saveImage:', error);
      } finally {
         setIsSaveLoading(false);
      }
   };

   const handleSkip = _.debounce(() => {
      handleSubmit();
      setFile(null);
      setOtp(null);
      onClose();
   }, 500);

   useEffect(() => {
      if (isOpen && skipBtnRef.current) {
         skipBtnRef.current.focus();
      }
   }, [isOpen]);

   // FIXED: Strict 4 image limit with better validation
   const handleImagesChange = (e, type) => {
      const files = Array.from(e.target.files);
      const currentImages = type === 'doc' ? docImages : type === 'user' ? userImages : invoiceImages;

      // Check limit BEFORE processing
      if (currentImages.length >= 4) {
         alert(
            `Maximum 4 ${
               type === 'doc' ? 'document' : type === 'user' ? 'user' : 'invoice'
            } images allowed. Please remove existing images first.`,
         );
         e.target.value = '';
         return;
      }

      if (currentImages.length + files.length > 4) {
         alert(`You can only add ${4 - currentImages.length} more image(s). Maximum limit is 4 images.`);
         e.target.value = '';
         return;
      }

      if (files.length > 0) {
         const file = files[0];
         const reader = new FileReader();
         reader.onloadend = () => {
            const preview = reader.result;
            setCurrentCropImage(preview);
            setCurrentCropType(type);
            setCurrentCropIndex(currentImages.length);
            setOriginalFile(file);
            setIsRecropping(false);
            setCropModalOpen(true);
         };
         reader.readAsDataURL(file);
      }

      e.target.value = '';
   };

   const handleRecrop = async (type, index, img) => {
      try {
         let imageToRecrop = img.preview || img.url;

         // For existing images from server, we need to fetch and convert to blob
         if (img.isExisting && img.url && !img.url.startsWith('blob:') && !img.url.startsWith('data:')) {
            try {
               const response = await fetch(img.url);
               const blob = await response.blob();
               imageToRecrop = URL.createObjectURL(blob);
            } catch (fetchError) {
               console.error('Failed to fetch image:', fetchError);
               alert('Failed to load image for cropping. The image may not be accessible due to CORS restrictions.');
               return;
            }
         }

         setCurrentCropImage(imageToRecrop);
         setCurrentCropType(type);
         setCurrentCropIndex(index);
         setOriginalFile(img.file || null);
         setIsRecropping(true);
         setCropModalOpen(true);
      } catch (error) {
         console.error('Error in handleRecrop:', error);
         alert('Failed to open crop editor. Please try again.');
      }
   };

   const handleCropComplete = async (croppedBlob, type, index) => {
      try {
         const croppedFile = new File([croppedBlob], `cropped-${Date.now()}.jpg`, {
            type: 'image/jpeg',
         });

         const preview = URL.createObjectURL(croppedBlob);

         const newImage = {
            file: croppedFile,
            preview: preview,
            isExisting: false,
         };

         if (isRecropping) {
            if (type === 'doc') {
               setDocImages((prev) => {
                  const oldImage = prev[index];
                  if (oldImage?.preview && !oldImage.isExisting) {
                     URL.revokeObjectURL(oldImage.preview);
                  }
                  const updated = [...prev];
                  updated[index] = newImage;
                  return updated;
               });
            } else if (type === 'user') {
               setUserImages((prev) => {
                  const oldImage = prev[index];
                  if (oldImage?.preview && !oldImage.isExisting) {
                     URL.revokeObjectURL(oldImage.preview);
                  }
                  const updated = [...prev];
                  updated[index] = newImage;
                  return updated;
               });
            } else if (type === 'invoice') {
               setInvoiceImages((prev) => {
                  const oldImage = prev[index];
                  if (oldImage?.preview && !oldImage.isExisting) {
                     URL.revokeObjectURL(oldImage.preview);
                  }
                  const updated = [...prev];
                  updated[index] = newImage;
                  return updated;
               });
            }
         } else {
            if (type === 'doc') {
               setDocImages((prev) => [...prev, newImage]);
            } else if (type === 'user') {
               setUserImages((prev) => [...prev, newImage]);
            } else if (type === 'invoice') {
               setInvoiceImages((prev) => [...prev, newImage]);
            }
         }

         setCropModalOpen(false);
         setCurrentCropImage(null);
         setIsRecropping(false);
         setOriginalFile(null);
         setCurrentCropType(null);
         setCurrentCropIndex(null);
      } catch (error) {
         console.error('Error handling crop complete:', error);
      }
   };

   const handleRemoveImage = async (type, index, img) => {
      if (img?.isExisting) {
         if (type === 'doc' || type === 'user') {
            await dispatch(deleteDocument({ id: String(accountId), imgName: img.url }));
         } else if (type === 'invoice') {
            if (entryType === 'sales') {
               await dispatch(deleteSalesDocument({ id: String(entryData.id), imgName: img.url }));
            } else if (entryType === 'purchase') {
               await dispatch(deletePurchaseDocument({ id: String(entryData.id), imgName: img.url }));
            } else if (entryType === 'issue') {
               await dispatch(deleteIssueDocument({ id: String(entryData.id), imgName: img.url }));
            } else if (entryType === 'receive') {
               await dispatch(deleteReceiveDocument({ id: String(entryData.id), imgName: img.url }));
            }
         }

         if (type === 'doc') {
            const updated = [...docImages];
            updated.splice(index, 1);
            setDocImages(updated);
         } else if (type === 'user') {
            const updated = [...userImages];
            updated.splice(index, 1);
            setUserImages(updated);
         } else {
            const updated = [...invoiceImages];
            updated.splice(index, 1);
            setInvoiceImages(updated);
         }

         if (type === 'doc' || type === 'user') {
            await dispatch(
               getAccount({
                  compId: Number(user?.loginhistory?.companyId),
                  accountTypes: ['K', 'E', 'C', 'D', 'BROKER', 'I', 'L', 'M', 'N', 'A'].join(','),
                  status: true,
               }),
            );
         }
      } else {
         // Revoke blob URL for memory cleanup
         if (img?.preview && !img.isExisting) {
            URL.revokeObjectURL(img.preview);
         }

         if (type === 'doc') {
            const updated = [...docImages];
            updated.splice(index, 1);
            setDocImages(updated);
         } else if (type === 'user') {
            const updated = [...userImages];
            updated.splice(index, 1);
            setUserImages(updated);
         } else {
            const updated = [...invoiceImages];
            updated.splice(index, 1);
            setInvoiceImages(updated);
         }
      }
   };

   // Cleanup blob URLs on unmount
   useEffect(() => {
      return () => {
         [...docImages, ...userImages, ...invoiceImages].forEach((img) => {
            if (img?.preview && !img.isExisting) {
               URL.revokeObjectURL(img.preview);
            }
         });
      };
   }, []);

   return (
      <>
         <ImageCropModal
            isOpen={cropModalOpen}
            onClose={() => setCropModalOpen(false)}
            imageSrc={currentCropImage}
            onCropComplete={handleCropComplete}
            imageType={currentCropType}
            imageIndex={currentCropIndex}
         />

         <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered finalFocusRef={skipBtnRef}>
            <ModalOverlay />
            <ModalContent
               bg={useColorModeValue('white', '#2d3748')}
               color={useColorModeValue('gray.800', 'white')}
               borderRadius="20px"
               p={5}
               boxShadow="lg">
               <ModalHeader fontSize="20px" fontWeight="600" mb={4}>
                  Customer Verification
               </ModalHeader>
               <ModalCloseButton />
               <ModalBody>
                  <Box>
                     <Grid gap={2}>
                        {/* User Images Section */}
                        <GridItem mb={3}>
                           <Flex justifyContent="space-between" alignItems="center" mb={1}>
                              <Text fontWeight="bold">Upload User Images</Text>
                              <Text fontSize="xs" color={userImages?.length >= 4 ? 'red.500' : 'gray.500'}>
                                 {userImages.length}/4 images
                              </Text>
                           </Flex>
                           <Input
                              type="file"
                              name="userImages"
                              onChange={(e) => handleImagesChange(e, 'user')}
                              placeholder="Upload user images..."
                              pt={1}
                              accept="image/*"
                              disabled={userImages.length >= 4}
                           />
                           {userImages.length >= 4 && (
                              <Text fontSize="xs" color="red.500" mt={1}>
                                 Maximum 4 images reached. Remove an image to add more.
                              </Text>
                           )}
                        </GridItem>
                        {userImages?.length > 0 && (
                           <Flex gap={4} wrap="wrap" mt={2} mb={4}>
                              {userImages.map((img, index) => (
                                 <Box key={index} position="relative" w="120px">
                                    <a
                                       href={img.isExisting ? img.url : img.preview}
                                       target="_blank"
                                       rel="noopener noreferrer"
                                       style={{ display: 'block' }}>
                                       <Image
                                          src={img.isExisting ? img.url : img.preview}
                                          alt={`User image ${index}`}
                                          crossOrigin="anonymous"
                                          boxSize="100px"
                                          objectFit="cover"
                                          borderRadius="md"
                                          border="1px solid"
                                          borderColor="gray.300"
                                          cursor="pointer"
                                          _hover={{ opacity: 0.8 }}
                                       />
                                    </a>
                                    <IconButton
                                       icon={<CloseIcon />}
                                       size="xs"
                                       colorScheme="red"
                                       position="absolute"
                                       top="-2"
                                       right="-2"
                                       onClick={() => handleRemoveImage('user', index, img)}
                                       borderRadius="full"
                                       aria-label="Remove user image"
                                    />
                                    <Button
                                       size="xs"
                                       colorScheme="blue"
                                       position="absolute"
                                       bottom="-2"
                                       left="50%"
                                       transform="translateX(-50%)"
                                       onClick={() => handleRecrop('user', index, img)}
                                       fontSize="10px"
                                       px={2}
                                       py={1}
                                       h="auto">
                                       Re-crop
                                    </Button>
                                 </Box>
                              ))}
                           </Flex>
                        )}

                        {/* Doc Images Section */}
                        <GridItem mb={3}>
                           <Flex justifyContent="space-between" alignItems="center" mb={1}>
                              <Text fontWeight="bold">Upload Doc Images</Text>
                              <Text fontSize="xs" color={docImages?.length >= 4 ? 'red.500' : 'gray.500'}>
                                 {docImages.length}/4 images
                              </Text>
                           </Flex>
                           <Input
                              type="file"
                              name="docImages"
                              onChange={(e) => handleImagesChange(e, 'doc')}
                              placeholder="Upload doc images..."
                              pt={1}
                              accept="image/*"
                              disabled={docImages.length >= 4}
                           />
                           {docImages.length >= 4 && (
                              <Text fontSize="xs" color="red.500" mt={1}>
                                 Maximum 4 images reached. Remove an image to add more.
                              </Text>
                           )}
                        </GridItem>
                        {docImages?.length > 0 && (
                           <Flex gap={4} wrap="wrap" mt={2} mb={4}>
                              {docImages.map((img, index) => (
                                 <Box key={index} position="relative" w="120px">
                                    <a
                                       href={img.isExisting ? img.url : img.preview}
                                       target="_blank"
                                       rel="noopener noreferrer"
                                       style={{ display: 'block' }}>
                                       <Image
                                          src={img.isExisting ? img.url : img.preview}
                                          alt={`Doc image ${index}`}
                                          crossOrigin="anonymous"
                                          boxSize="100%"
                                          objectFit="cover"
                                          borderRadius="md"
                                          border="1px solid"
                                          borderColor="gray.300"
                                          cursor="pointer"
                                          _hover={{ opacity: 0.8 }}
                                       />
                                    </a>
                                    <IconButton
                                       icon={<CloseIcon />}
                                       size="xs"
                                       colorScheme="red"
                                       position="absolute"
                                       top="-2"
                                       right="-2"
                                       onClick={() => handleRemoveImage('doc', index, img)}
                                       borderRadius="full"
                                       aria-label="Remove doc image"
                                    />
                                    <Button
                                       size="xs"
                                       colorScheme="blue"
                                       position="absolute"
                                       bottom="-2"
                                       left="50%"
                                       transform="translateX(-50%)"
                                       onClick={() => handleRecrop('doc', index, img)}
                                       fontSize="10px"
                                       px={2}
                                       py={1}
                                       h="auto">
                                       Re-crop
                                    </Button>
                                 </Box>
                              ))}
                           </Flex>
                        )}

                        {/* Invoice Images Section */}
                        <GridItem mb={3}>
                           <Flex justifyContent="space-between" alignItems="center" mb={1}>
                              <Text fontWeight="bold">Upload Invoice Images</Text>
                              <Text fontSize="xs" color={invoiceImages?.length >= 4 ? 'red.500' : 'gray.500'}>
                                 {invoiceImages?.length || 0}/4 images
                              </Text>
                           </Flex>
                           <Input
                              type="file"
                              name="invoiceImage"
                              onChange={(e) => handleImagesChange(e, 'invoice')}
                              placeholder="Upload invoice images..."
                              pt={1}
                              accept="image/*"
                              disabled={invoiceImages.length >= 4}
                           />
                           {invoiceImages.length >= 4 && (
                              <Text fontSize="xs" color="red.500" mt={1}>
                                 Maximum 4 images reached. Remove an image to add more.
                              </Text>
                           )}
                        </GridItem>
                        {invoiceImages?.length > 0 && (
                           <GridItem mb={3}>
                              <Flex gap={3} wrap="wrap">
                                 {invoiceImages.map((img, index) => (
                                    <Box key={index} position="relative" width="120px" height="120px" flexShrink={0}>
                                       <a
                                          href={img.isExisting ? img.url : img.preview}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{ display: 'block', width: '100%', height: '100%' }}>
                                          <Image
                                             src={img.isExisting ? img.url : img.preview}
                                             alt={`Invoice image ${index}`}
                                             crossOrigin="anonymous"
                                             width="100%"
                                             height="100%"
                                             objectFit="cover"
                                             borderRadius="md"
                                             border="2px solid"
                                             borderColor="gray.300"
                                             cursor="pointer"
                                             _hover={{ opacity: 0.8 }}
                                          />
                                       </a>
                                       <IconButton
                                          icon={<CloseIcon />}
                                          size="xs"
                                          colorScheme="red"
                                          position="absolute"
                                          top="-8px"
                                          right="-8px"
                                          onClick={() => handleRemoveImage('invoice', index, img)}
                                          borderRadius="full"
                                          aria-label="Remove Invoice image"
                                          zIndex={2}
                                       />
                                       <Button
                                          size="xs"
                                          colorScheme="blue"
                                          position="absolute"
                                          bottom="-8px"
                                          left="50%"
                                          transform="translateX(-50%)"
                                          onClick={() => handleRecrop('invoice', index, img)}
                                          fontSize="10px"
                                          px={2}
                                          py={1}
                                          h="auto"
                                          zIndex={2}>
                                          Re-crop
                                       </Button>
                                    </Box>
                                 ))}
                              </Flex>
                           </GridItem>
                        )}
                     </Grid>
                  </Box>

                  {/* OTP Verification Section */}
                  {mobile ? (
                     <Box my={5}>
                        <Heading as="h3" size="md" mb={5}>
                           Verify By OTP
                        </Heading>
                        <Grid gap={2}>
                           <GridItem>
                              <Flex gap={3}>
                                 <Input
                                    type="text"
                                    value={hideMobileNumber(mobile)}
                                    placeholder="Enter Mobile No."
                                    pt={1}
                                    isDisabled
                                 />
                                 <Button
                                    colorScheme="blue"
                                    variant="solid"
                                    onClick={() => dispatch(sendVerifyOtp({ id: accountId }))}>
                                    Send OTP
                                 </Button>
                              </Flex>
                           </GridItem>
                           <GridItem>
                              <HStack>
                                 <PinInput defaultValue="" onChange={(value) => setOtp(value)}>
                                    <PinInputField />
                                    <PinInputField />
                                    <PinInputField />
                                    <PinInputField />
                                    <PinInputField />
                                    <PinInputField />
                                 </PinInput>
                              </HStack>
                           </GridItem>
                        </Grid>
                     </Box>
                  ) : null}
               </ModalBody>

               {/* Modal Footer */}
               <ModalFooter justifyContent="flex-end" alignItems="center">
                  <Flex gap={3}>
                     <Button
                        ref={skipBtnRef}
                        colorScheme="blue"
                        variant="ghost"
                        onClick={() => handleSkip()}
                        _focus={{ outlineColor: 'blue.500' }}
                        autoFocus
                        isLoading={isLoading}
                        disabled={isLoading || isSaveLoading}>
                        Skip
                     </Button>
                     <Button
                        colorScheme="green"
                        variant="solid"
                        onClick={() => saveImage()}
                        _focus={{ outlineColor: 'blue.500' }}
                        isLoading={isSaveLoading}
                        loadingText="Saving..."
                        disabled={
                           isSaveLoading ||
                           (docImages?.length > 0 || userImages?.length > 0 || (otp && otp.length === 6) ? false : true)
                        }>
                        Save
                     </Button>
                     <Button
                        colorScheme="red"
                        variant="solid"
                        onClick={() => onClose()}
                        _focus={{ outlineColor: 'blue.500' }}
                        disabled={isSaveLoading}>
                        Cancel
                     </Button>
                  </Flex>
               </ModalFooter>
            </ModalContent>
         </Modal>
      </>
   );
};

export default CustomerVerifyModal;
